import { Plus, X, AlertTriangle } from 'lucide-react';
import { uid } from '../../lib/uid';
import { Button } from '../ui/button';
import { formatCurrency } from '../../lib/defaultData';

export default function PaymentSchedule({
  paymentSchedule,
  onUpdate,
  paymentTotal,
  retentionPercentage,
  hasWarning,
}) {
  const updateNode = (index, field, value) => {
    const updated = paymentSchedule.map((node, i) =>
      i === index ? { ...node, [field]: value } : node
    );
    onUpdate(updated);
  };

  const addNode = () => {
    onUpdate([...paymentSchedule, { id: uid(), description: '', percentage: 0 }]);
  };

  const removeNode = (index) => {
    onUpdate(paymentSchedule.filter((_, i) => i !== index));
  };

  const retentionAmount = (retentionPercentage / 100) * paymentTotal;

  return (
    <div
      className="py-8 border-t"
      style={{ borderColor: 'rgba(201,168,76,0.2)' }}
      data-testid="payment-schedule"
    >
      <h2 className="section-heading">Payment Schedule</h2>

      {/* Header */}
      <div className="payment-row text-xs font-semibold uppercase tracking-wider text-[#6b7280]">
        <span>Description</span>
        <span className="text-right">%</span>
        <span className="text-right">Amount (AUD)</span>
        <span />
      </div>

      {/* Editable nodes */}
      {paymentSchedule.map((node, idx) => (
        <div key={idx} className="payment-row" data-testid={`payment-node-${idx}`}>
          <input
            value={node.description}
            onChange={e => updateNode(idx, 'description', e.target.value)}
            className="editable-field text-sm"
            placeholder="Payment milestone description"
            data-testid={`payment-desc-${idx}`}
          />
          <div className="relative">
            <input
              type="number"
              value={node.percentage}
              onChange={e => updateNode(idx, 'percentage', parseFloat(e.target.value) || 0)}
              className="editable-field amount-input text-sm w-full"
              min="0"
              max="100"
              data-testid={`payment-pct-${idx}`}
            />
            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[#6b7280] text-xs">%</span>
          </div>
          <span className="font-mono text-sm text-right font-medium" data-testid={`payment-amount-${idx}`}>
            {formatCurrency((node.percentage / 100) * paymentTotal)}
          </span>
          <button
            onClick={() => removeNode(idx)}
            className="text-[#6b7280] hover:text-[#dc2626] transition-colors"
            data-testid={`payment-delete-${idx}`}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ))}

      {/* Retention Row (fixed) */}
      <div
        className="payment-row payment-row-retention"
        data-testid="payment-retention-row"
      >
        <span className="text-sm font-medium text-[#2c2c2c]">
          Retention after defects been completed
        </span>
        <div className="text-right">
          {hasWarning ? (
            <span
              className="text-xs font-semibold flex items-center justify-end gap-1"
              style={{ color: '#dc2626' }}
              data-testid="retention-warning"
            >
              <AlertTriangle className="w-3 h-3" />
              {retentionPercentage.toFixed(1)}%
            </span>
          ) : (
            <span
              className="text-sm font-mono font-medium"
              style={{ color: '#6b7280' }}
              data-testid="retention-pct"
            >
              {retentionPercentage.toFixed(1)}%
            </span>
          )}
        </div>
        <span className="font-mono text-sm text-right font-medium" data-testid="retention-amount">
          {hasWarning ? '—' : formatCurrency(retentionAmount)}
        </span>
        <span />
      </div>

      {hasWarning && (
        <div
          className="flex items-center gap-2 mt-3 px-4 py-2 rounded text-sm font-medium"
          style={{ background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' }}
          data-testid="payment-warning-banner"
        >
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          Exceeds 100%. Please check milestone percentages.
        </div>
      )}

      <Button
        variant="outline"
        size="sm"
        onClick={addNode}
        className="mt-4 border-[#c9a84c] text-[#c9a84c] hover:bg-[#c9a84c]/10"
        data-testid="add-payment-node-btn"
      >
        <Plus className="w-4 h-4 mr-1" />
        Add Milestone
      </Button>
    </div>
  );
}
